import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { SPACE_SOFT_DELETE_DAYS } from '@gitbook/app-database';
import { DateShortRelative, IntlPluralize } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
    LoadingIndicator,
    toast,
} from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import { useSetAnalyticsScreen } from '../../../navigation';
import { apiClient } from '../../../state';

/**
 * Full-screen component when space has been soft-deleted.
 */
export function SpaceDeleted(props: { space: api.Space }): React.ReactElement {
    const { space } = props;

    assertIsDefined(space.deletedAt);
    const deletionDate = new Date(space.deletedAt);
    const remainingDays =
        SPACE_SOFT_DELETE_DAYS -
        Math.round((Date.now() - deletionDate.getTime()) / (24 * 60 * 60 * 1000));
    const [loading, wrapLoading] = useLoadingState();

    useSetAnalyticsScreen(analytics.createSpaceEvent(space, { screen: 'space_restore' }), [
        space.id,
    ]);

    const onPressRestore = async () => {
        await toast.catch(async () => {
            await apiClient.spaces.restoreSpace(space.id);
            toast.success('Welcome back!', {
                description: 'This space has been restored and can now be accessed again.',
            });
        }, 'Error while restoring space');
    };

    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Space />
            </EmptyStateIcon>
            <Heading>Space deleted</Heading>
            <Description>
                This space has been set for deletion{' '}
                <DateShortRelative value={deletionDate} onlyDate={false} />. You have{' '}
                {remainingDays} <IntlPluralize count={remainingDays} singular="day" plural="days" />{' '}
                to restore it before it's fully deleted from our system.
            </Description>
            <EmptyStateActions>
                <>
                    <Button
                        kind="secondary"
                        size="medium"
                        href={{ screen: { type: 'home' } }}
                        label="Take me home"
                    />
                    {space.permissions.admin ? (
                        <Button
                            label="Restore"
                            kind="primary"
                            size="medium"
                            trailingIcon={loading ? LoadingIndicator : undefined}
                            disabled={loading}
                            onPress={() => {
                                return wrapLoading(async () => {
                                    await onPressRestore();
                                });
                            }}
                        />
                    ) : null}
                </>
            </EmptyStateActions>
        </EmptyState>
    );
}
